import React, { useRef } from "react";
import { MediaItem } from "../../../../types/item";
import { ImSpinner2 } from "react-icons/im";
import ASSET_PATHS from "../../../../assets/config/assetPaths";
import MediaThumbnail from "./MediaThumbnail";
// import { FiTrash2 } from "react-icons/fi";

interface MediaUploadSectionProps {
  media: MediaItem[];
  onMediaUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  mediaUploadProgress: { completed: number; mediaTotal: number };
  onMediaRemove: (e: React.MouseEvent, uuid: string) => void;
  onViewMedia: (media: MediaItem) => void;
  onViewAllMedia: () => void;
  isUploadingMedia: boolean;
  allowUpload?: boolean;
}

const MediaUploadSection: React.FC<MediaUploadSectionProps> = ({
  media,
  onMediaUpload,
  mediaUploadProgress,
  // onMediaRemove,
  onViewMedia,
  onViewAllMedia,
  isUploadingMedia,
  allowUpload = false,
}) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const displayLimit = 6;

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Media</h3>

      {/* Media Cards Grid Preview */}
      <div className="flex flex-wrap gap-1">
        {/* Add Media Button Card */}
        {allowUpload && (
          <div className="flex-shrink-0">
            <div className="relative">
              <button
                onClick={() => imageInputRef.current?.click()}
                className="w-24 h-24 border-2 border-dashed border-blue-300 rounded-md
                                flex flex-col items-center justify-center bg-blue-50 
                                hover:bg-blue-100 hover:border-blue-400 transition-colors cursor-pointer"
              >
                <img
                  src={ASSET_PATHS.blueCameraIcon}
                  alt="Add Media"
                  className="h-6 w-6 text-blue-600 mb-1"
                />
                <span className="text-xs text-blue-600 font-medium">
                  Add Media
                </span>
              </button>

              {/* Single file input for OS native media selection */}
              <input
                ref={imageInputRef}
                id="media-upload"
                type="file"
                accept="image/*,video/*"
                multiple={true}
                className="hidden"
                onChange={onMediaUpload}
                disabled={isUploadingMedia}
              />
            </div>
          </div>
        )}
        {!allowUpload && media.length == 0 && (
          <div className="flex-shrink-0 text-center w-full">
            <div className="relative">
              <p className="text-gray-500">No media.</p>
            </div>
          </div>
        )}

        {media.length > 0 &&
          media
            .slice(0, displayLimit)
            .map((mediaItem: MediaItem, index: number) => {
              return (
                <div
                  key={mediaItem.id || index}
                  className="relative rounded-sm overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative cursor-pointer w-24 h-24"
                  onClick={() => onViewMedia(mediaItem)}
                >
                  <MediaThumbnail
                    mediaItem={mediaItem}
                    onError={() => {}} // Error handling is done within the component
                  />
                </div>
              );
            })}

        {/* Show +count square if there are more than displayLimit media items */}
        {media.length > displayLimit && (
          <div
            className="w-24 h-24 border border-1 border-gray-300 rounded-md
          flex flex-col items-center justify-center bg-primary-100 
          hover:bg-blue-100 hover:border-blue-400 transition-colors cursor-pointer"
            onClick={onViewAllMedia}
          >
            <span className="text-2xl font-semibold text-gray-600">
              +{media.length - displayLimit}
            </span>
            {/* <div className="text-xs text-blue-600 mt-3">View All</div> */}
          </div>
        )}
      </div>

      {/* View Gallery Button - appears when there's media */}
      {media.length > 0 && (
        <div className="mt-4">
          <button
            onClick={onViewAllMedia}
            className="w-full cursor-pointer transition-colors flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
            <span className="font-medium">View All Media ({media.length})</span>
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {isUploadingMedia && (
        <div className="mt-3 text-center">
          <div className="inline-flex items-center gap-2 text-blue-600">
            <ImSpinner2 className="animate-spin" />
            <span className="text-sm">
              {mediaUploadProgress.completed}/{mediaUploadProgress.mediaTotal}
            </span>
            <span className="text-sm">Processing media...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploadSection;
